import React, { memo } from 'react';
import { formatTimeAgo, getStatusConfig } from '../utils';
import { Button } from '../../../components/ui/button';
import { cn } from '@/lib/utils';
import { SnapshotPreview } from './SnapshotPreview';
import { AlertPreviewDto, AlertType } from '../types';
import { Building2, Calendar, User, Loader2, Clock } from 'lucide-react';
import { Time } from '@/components/ui/time';

interface AlertCardProps {
  alert: AlertPreviewDto;
  isSelected: boolean;
  onTakeAlert: (id: string) => void;
  isTaking: boolean;
  type: AlertType;
  'data-alert-index'?: number;
  onClick?: () => void;
}

export const AlertCard = memo(
  ({
    alert,
    isSelected,
    onTakeAlert,
    isTaking,
    type,
    'data-alert-index': dataAlertIndex,
    onClick,
  }: AlertCardProps) => {
    const statusConfig = getStatusConfig(alert.status);

    const handleTake = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (isTaking || !alert.id) return;
        onTakeAlert(alert.id);
      },
      [alert.id, onTakeAlert, isTaking]
    );

    return (
      <a
        href={`/alert/${alert.id}`}
        className={cn(
          'relative w-full rounded-lg border border-[#2E3835] bg-card2 shadow-sm',
          isSelected && 'border-[#B7D257]',
          isTaking && 'cursor-not-allowed opacity-50'
        )}
        onClick={handleTake}
        data-alert-index={dataAlertIndex}
        aria-disabled={isTaking}
      >
        {isTaking && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/50">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        )}
        <div
          onClick={onClick}
          className="flex flex-col items-center justify-between rounded-t-lg border-[#2E3835] hover:bg-[#1a201f]"
        >
          <div className="flex w-full items-center justify-between rounded-t-lg border-b border-[#2E3835] p-4 max-lg:flex-col max-lg:items-start max-lg:gap-2">
            <h3 className="truncate font-semibold text-[#B7D257]">{alert.alertId}</h3>

            <aside
              className={`flex-shrink-0 rounded-lg border border-[#484848] px-4 py-1 font-semibold text-white ${statusConfig.color}`}
            >
              {statusConfig.name}
            </aside>
          </div>
          <div className="flex w-full items-center justify-between border-b border-[#2E3835] p-4 text-sm text-gray-300 flex-wrap gap-2">
            <p
              className="flex flex-col items-start gap-1 whitespace-nowrap text-sm text-gray-300"
              title={`${alert.companyName || 'Unnamed Company'} · ${alert.projectName || 'Unnamed Project'}`}
            >
              <span className="flex items-center gap-1">
                <User size={16} className="flex-shrink-0 opacity-40" />
                <span className="text-wrap">{alert.companyName || 'Unnamed Company'}</span>
              </span>

              <span className="flex items-center gap-1">
                <Building2 size={16} className="flex-shrink-0 opacity-40" />
                <span className="text-wrap">{alert.projectName || 'Unnamed Project'}</span>
              </span>
            </p>
            <span className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4 opacity-40" /> {' '}
                <Time date={alert.lastEvent} />
              </div>
              {type !== 'new' ? (
                <div className="flex items-center gap-1">
                <User size={16}className="h-4 w-4 opacity-40"  />  <span>{alert.actionedBy}</span> 
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 opacity-40" /> <p className="text-sm text-gray-300">{formatTimeAgo(alert.lastEvent)}</p>{' '}
                 
                </div>
              )}
            </span>
          </div>
          <div className="w-full">
            <SnapshotPreview src={alert.snapshot || ''} />
          </div>
        </div>
        {isSelected && (
          <Button
            onClick={handleTake}
            className="bg-red absolute right-[-12px] top-[-18px] rounded-full p-1"
            hotkey={{ keyboard: 'enter' }}
            disabled={isTaking}
          />
        )}
      </a>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.alert.id === nextProps.alert.id &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.alert.status === nextProps.alert.status &&
      prevProps.alert.occurred === nextProps.alert.occurred
    );
  }
);
